import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import {
  useMutationUpdateResource,
  useQuerySearchWorkspaceShares,
  useAppContext,
} from "@examspark/common-app";
import { resourceVisibilityList } from "@examspark/common-meta";
import {
  CheckboxListInput,
  ModalProps,
  SubmitButtons,
} from "@examspark/common-ui";
import { Resource, ResourceVisibilityType } from "@examspark/types";

import { getVisibilityDescription } from "../../utils";
import Modal from "../common/Modal";

const IconLabel = styled.div`
  svg {
    margin-right: 1rem;
  }
`;

const VisibilityModal = ({
  resource,
  onRequestClose,
  ...props
}: Pick<ModalProps, "isOpen" | "onRequestClose"> & {
  resource: Resource | undefined;
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [visibility, setVisibility] = useState(
    resource?.visibility ?? ResourceVisibilityType.WorkspaceShareable,
  );
  const [state] = useAppContext();

  const updateResource = useMutationUpdateResource();
  const consumerWorkspaceShares = useQuerySearchWorkspaceShares({
    publisherWorkspaceId: state.workspaceUser?.id,
  });

  const showWorkspaceShareable =
    !!consumerWorkspaceShares?.data?.items?.length ||
    visibility === ResourceVisibilityType.WorkspaceShareable;

  useEffect(
    () =>
      setVisibility(
        resource?.visibility ?? ResourceVisibilityType.WorkspaceShareable,
      ),
    [resource],
  );

  const saveVisibility = useCallback(async () => {
    if (resource == null) {
      onRequestClose?.();
      return;
    }

    setIsSaving(true);
    try {
      await updateResource.mutateAsync({
        id: resource.id,
        sk: resource.sk,
        visibility,
      });
    } finally {
      setIsSaving(false);
      onRequestClose?.();
    }
  }, [onRequestClose, resource, updateResource, visibility]);

  const visibilities = resourceVisibilityList.map(getVisibilityDescription);

  return (
    <Modal
      {...props}
      onRequestClose={onRequestClose}
      title="Set Resource Visibility"
      buttons={
        <SubmitButtons
          submitLabel="Save"
          isSubmitting={isSaving}
          onSubmit={() => saveVisibility()}
          onCancel={() => onRequestClose?.()}
        />
      }
    >
      <Modal.PaddedBody>
        <CheckboxListInput>
          {visibilities.map((v) => {
            if (
              v.visibility === ResourceVisibilityType.WorkspaceShareable &&
              !showWorkspaceShareable
            ) {
              return null;
            }

            return (
              <CheckboxListInput.Item
                key={v.visibility}
                checked={visibility === v.visibility}
                onClick={() => setVisibility(v.visibility)}
                label={
                  <IconLabel>
                    <v.IconComponent width="14" />
                    {v.label}
                  </IconLabel>
                }
              />
            );
          })}
        </CheckboxListInput>
      </Modal.PaddedBody>
    </Modal>
  );
};

export default VisibilityModal;
